import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useWatch, type Control } from 'react-hook-form';

import { type PartnerOfferActionKind } from '@/api/routes/partners/partners.offers.api';
import { IconArrowUp } from '@/components/icon/tabler/IconArrowUp';
import { IconTrash } from '@/components/icon/tabler/IconTrash';
import OfferActionIcon from '@/domain/offers/components/OfferActionIcon';
import OfferFormField from '@/domain/offers/components/OfferFormField';
import { type OfferFormValues } from '@/domain/offers/types';
import {
  OFFER_LIMITS,
  getOfferWhatsappDefaultMessage,
  offerActionDefaultLabel,
  offerActionKindLabel,
} from '@/domain/offers/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  control: Control<OfferFormValues>;
  index: number;
  kind: PartnerOfferActionKind;
  onRemove: () => void;
  /** Only for the second button: it becomes the first. */
  onMoveUp?: () => void;
}

/** One button: only the fields of its kind, plus the optional text. */
const OfferActionCard = ({ control, index, kind, onRemove, onMoveUp }: Props) => {
  const t = useTranslate('domain.offers.components.OfferActionCard');
  const title = useWatch({ control, name: 'title' });

  return (
    <Box sx={sx.card}>
      <Stack direction="row" alignItems="center" gap={1.25}>
        <Box sx={sx.icon}>
          <OfferActionIcon kind={kind} fontSize="small" />
        </Box>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {t('position', { n: index + 1 })}
          </Typography>
          <Typography variant="subtitle2" fontWeight={700}>
            {offerActionKindLabel[kind]}
          </Typography>
        </Stack>
        {onMoveUp ? (
          <Tooltip title={t('moveUp')}>
            <IconButton size="small" aria-label={t('moveUp')} onClick={onMoveUp}>
              <IconArrowUp fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
        <Tooltip title={t('remove')}>
          <IconButton size="small" aria-label={t('remove')} onClick={onRemove}>
            <IconTrash fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack gap={2.5}>
        {kind === 'link' ? (
          <OfferFormField
            control={control}
            name={`actions.${index}.url`}
            label={t('url')}
            placeholder="https://"
            inputMode="url"
            hint={t('urlHint')}
          />
        ) : null}
        {kind === 'whatsapp' ? (
          <>
            <OfferFormField
              control={control}
              name={`actions.${index}.number`}
              label={t('whatsappNumber')}
              placeholder="(83) 99999-0000"
              inputMode="tel"
            />
            <OfferFormField
              control={control}
              name={`actions.${index}.message`}
              label={t('message')}
              optionalLabel={t('optional')}
              placeholder={getOfferWhatsappDefaultMessage(title ?? '')}
              maxChars={OFFER_LIMITS.whatsappMessageMax}
              hint={t('messageHint')}
              multiline
              minRows={2}
            />
          </>
        ) : null}
        {kind === 'phone' ? (
          <OfferFormField
            control={control}
            name={`actions.${index}.number`}
            label={t('phoneNumber')}
            placeholder="(83) 3222-0000"
            inputMode="tel"
            hint={t('phoneHint')}
          />
        ) : null}
        {kind === 'lead' ? (
          <Typography variant="caption" color="text.secondary">
            {t('leadHint')}
          </Typography>
        ) : null}
        {kind === 'directions' ? (
          <Typography variant="caption" color="text.secondary">
            {t('directionsHint')}
          </Typography>
        ) : null}
        <OfferFormField
          control={control}
          name={`actions.${index}.label`}
          label={t('label')}
          optionalLabel={t('optional')}
          placeholder={offerActionDefaultLabel[kind]}
          maxChars={OFFER_LIMITS.actionLabelMax}
          hint={t('labelHint')}
        />
      </Stack>
    </Box>
  );
};

export default OfferActionCard;

const sx = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    p: 2,
    border: 1,
    borderColor: 'divider',
    borderRadius: radiusTokens.xl,
  },
  icon: {
    width: 36,
    height: 36,
    borderRadius: radiusTokens.lg,
    bgcolor: 'action.hover',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
};
